import { compact, get, isEmpty, map, reject, size, split, trim } from "lodash";
import React, { useEffect, useState } from "react";

import type {
  NotificationChannel,
  NotificationConfig,
  SessionType,
} from "@platform/dmv-api";

import ButtonPrimary from "../ButtonPrimary";
import Modal from "../Modal";
import Toggle from "../Toggle";

// Local copies: CRA webpack cannot resolve @platform/dmv-api at runtime.
const NOTIFICATION_CHANNELS = [
  "slack",
  "email",
];
const SESSION_TYPES = [
  "ignition.created",
  "ignition.opened",
  "ignition.completed",
  "ignition.attempted",
  "ignition.exited",
  "ignition.modified",
  "account.modified",
  "account.retrying",
  "account.disconnected",
  "policy.modified",
  "policy.renewal-available",
  "policy-action.request-initiated",
  "policy-action.request-succeeded",
  "policy-action.policy-refresh-succeeded",
];

type NotificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notification: NotificationConfig) => void;
  initialData?: NotificationConfig | null;
};

const inputClassName =
  "w-full rounded border border-gray-300 p-2.5 text-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black";

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const hasInvalidSeparators = (input: string): boolean => {
  return /[;|]/.test(input);
};

const NotificationModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}: NotificationModalProps) => {
  const isEditMode = !!initialData;

  const [channel, setChannel] = useState<NotificationChannel>("slack");
  const [type, setType] = useState<SessionType>(SESSION_TYPES[0] as SessionType);
  const [enabled, setEnabled] = useState(true);
  const [locationsInput, setLocationsInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && initialData) {
      setChannel(get(initialData, "channel", "slack"));
      setType(get(initialData, "type", SESSION_TYPES[0]) as SessionType);
      setEnabled(get(initialData, "enabled", true));
      setLocationsInput(get(initialData, "locations", []).join(", "));
      setValidationError(null);
    } else if (isOpen && !initialData) {
      setChannel("slack");
      setType(SESSION_TYPES[0] as SessionType);
      setEnabled(true);
      setLocationsInput("");
      setValidationError(null);
    }
  }, [isOpen, initialData]);

  const validateLocations = (): string | null => {
    if (!locationsInput.trim()) {
      return null;
    }

    if (hasInvalidSeparators(locationsInput)) {
      return "Use commas to separate multiple values (not ; or |)";
    }

    const locations = compact(map(split(locationsInput, ","), trim));

    if (channel === "email") {
      const invalidEmails = reject(locations, isValidEmail);
      if (!isEmpty(invalidEmails)) {
        return `Invalid email${size(invalidEmails) > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`;
      }
    } else if (channel === "slack") {
      const invalidUrls = reject(locations, isValidUrl);
      if (!isEmpty(invalidUrls)) {
        return `Invalid URL${size(invalidUrls) > 1 ? "s" : ""}: ${invalidUrls.join(", ")}`;
      }
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateLocations();
    if (error) {
      setValidationError(error);
      return;
    }

    const locations = locationsInput
      .split(",")
      .map((loc) => loc.trim())
      .filter(Boolean);

    onSave({
      channel,
      type,
      enabled,
      locations,
    });

    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Notification" : "Add Notification"}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">Channel</label>
          <select
            value={channel}
            onChange={(e) =>
              setChannel(e.target.value as NotificationChannel)
            }
            className={inputClassName}
          >
            {NOTIFICATION_CHANNELS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">
            Event Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as SessionType)}
            className={inputClassName}
          >
            {SESSION_TYPES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-y-2">
          <label className="text-sm font-medium text-gray-700">
            Locations
            <span className="font-normal text-gray-500 ml-1">
              (comma-separated)
            </span>
          </label>
          <input
            type="text"
            value={locationsInput}
            onChange={(e) => {
              setLocationsInput(e.target.value);
              setValidationError(null);
            }}
            placeholder={
              channel === "slack"
                ? "https://hooks.slack.com/services/..."
                : "email@example.com, another@example.com"
            }
            className={`${inputClassName} ${validationError ? "border-red-500 focus:ring-red-500" : ""}`}
          />
          {validationError && (
            <p className="text-sm text-red-600">{validationError}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Enabled</label>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <ButtonPrimary
            text={isEditMode ? "Save" : "Add"}
            type="submit"
            icon={undefined}
            className=""
          />
        </div>
      </form>
    </Modal>
  );
};

export default NotificationModal;
